import React, { useContext, useState, useEffect } from "react";
import {
  Button,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  CardHeader,
  useTheme,
} from "@mui/material";
import { PageContent, QuestionnaireGroup } from "../../components";
import { useNavigate } from "react-router-dom";
import { AppContext } from "../../context/AppContext";
import lwn_data from "../../data";
import { useTranslation } from "react-i18next";

const ScreeningResults = () => {
  const {
    questionnaires,
    isLoading,
    ranking,
    prioritizedYesIndexes,
    setPrioritizedYesIndexes,
    changeCurrentStep,
    getRankings,
    allAnswered,
    removeRankingAndIndexes,
  } = useContext(AppContext);

  const [identifiedBarriers, setIdentifiedBarriers] = useState([]);
  const [barriersNotApply, setBarriersNotApply] = useState([]);
  const [barriersUnsure, setBarriersUnsure] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();

  useEffect(() => {
    if (questionnaires.length !== 0) {
      const questionnaire = questionnaires.find((q) => q.questionnaireId === "0");

      if (questionnaire === undefined || questionnaire.answers === undefined) {
        return navigate("/");
      }

      const answers = questionnaire.answers;

      const newIdentifiedBarriers = answers
        .filter((answer) => answer.text === "0")
        .map((answer, index) => ({ text: answer.questionText, index }));

      const newBarriersNotApply = answers
        .filter((answer) => answer.text === "1")
        .map((answer) => answer.questionText);

      const newBarriersUnsure = answers
        .filter((answer) => answer.text === "2")
        .map((answer) => answer.questionText);

      setBarriersNotApply(newBarriersNotApply);
      setBarriersUnsure(newBarriersUnsure);
      setIdentifiedBarriers(newIdentifiedBarriers);
      setLoading(false);
    }
  }, [navigate, questionnaires]);

  useEffect(() => {
    const requiredRankings = Math.min(3, identifiedBarriers.length);
    setIsValid(ranking.length >= requiredRankings);
  }, [identifiedBarriers.length]);

  useEffect(() => {
    if (prioritizedYesIndexes.length > 0) {
      getRankings();
    }
  }, [prioritizedYesIndexes]);

  const handleIdentifiedRankingChange = (updatedRankings) => {
    const maxRanking = Math.min(3, identifiedBarriers.length);
    const validRankings = updatedRankings.map((r) =>
      r.rank > maxRanking || r.rank === undefined ? "" : r.rank
    );

    const prioritizedIndexes = validRankings
      .map((rank, index) => ({ rank, index }))
      .filter(({ rank }) => rank !== "")
      .sort((a, b) => a.rank - b.rank)
      .map(({ index }) => identifiedBarriers[index].index);

    setPrioritizedYesIndexes(prioritizedIndexes);
  };

  const handleBackClick = async () => {
    await removeRankingAndIndexes();
    navigate("/BarrierQuestions/0");
    changeCurrentStep(1);
  };

  const handleNextClick = () => {
    const totalIdentifiedBarriers = identifiedBarriers.length;

    if (totalIdentifiedBarriers === 1 && ranking.length === 0) {
      navigate(`/IdentifiedBarriers`);
      changeCurrentStep(3);
      return;
    }

    const nextQuestionnaire = ranking.map((rank) => {
      const matchedQuestionnaire = lwn_data.find((q) => q.questionnaireType === rank.type);
      return {
        ...rank,
        questionnaireId: matchedQuestionnaire ? matchedQuestionnaire.questionnaireId : null,
        completed: matchedQuestionnaire ? allAnswered(matchedQuestionnaire.questionnaireId) : false,
      };
    });
    // let nextIndex = -1;
    // nextIndex = nextQuestionnaire.findIndex((questionnaire) => questionnaire.completed === false);
    // if (nextIndex === -1) {
    //   navigate(`/ActionPlan`);
    //   changeCurrentStep(4);
    // } else {
    navigate(`/IdentifiedBarriers`);
    changeCurrentStep(3);
    // }
  };

  if (loading && isLoading && questionnaires.length === 0) {
    return <div>{t("message_loading")}</div>;
  }

  return (
    <PageContent title={t("title_screening_results")}>
      <Box mb={2}>
        <Typography variant="body1">{t("below_are_your_answers")}</Typography>
      </Box>
      <Card
        elevation={0}
        style={{
          backgroundColor: "#f9f9f9",
          borderStyle: "solid",
          borderWidth: "2px",
          borderColor: theme.palette.secondary.main,
        }}
        sx={{ mb: 2 }}
      >
        <CardHeader
          title={t("screening_results_card_title1")}
          sx={{
            backgroundColor: theme.palette.secondary.light,
          }}
        />
        <CardContent>
          <QuestionnaireGroup
            questions={identifiedBarriers.map((barrier) => barrier.text)}
            enableRanking={true}
            onRankingChange={handleIdentifiedRankingChange}
            stateRanking={ranking}
            prioritizedYesIndexes={prioritizedYesIndexes}
            setIsValid={setIsValid}
          />
        </CardContent>
      </Card>
      <Card
        elevation={0}
        style={{
          //backgroundColor: colors.lightGray,
          borderStyle: "solid",
          borderWidth: "1px",
          //borderColor: colors.darkGray,
        }}
        sx={{ mb: 2 }}
      >
        <CardHeader
          title={t("screening_results_card_title2")}
          titleTypographyProps={{
            fontSize: "1.25rem", // Set font size
          }}
          sx={{
            backgroundColor: "rgba(51, 51, 51, 0.2)", // Dark gray background with 80% opacity
          }}
        />
        <CardContent>
          <QuestionnaireGroup questions={barriersUnsure} />
        </CardContent>
      </Card>
      <Card
        elevation={0}
        style={{
          //backgroundColor: colors.lightGray,
          borderStyle: "solid",
          borderWidth: "1px",
          //borderColor: colors.darkGray,
        }}
        sx={{ mb: 2 }}
      >
        <CardHeader
          title={t("screening_results_card_title3")}
          titleTypographyProps={{
            fontSize: "1.25rem", // Set font size
          }}
          sx={{
            backgroundColor: "rgba(51, 51, 51, 0.2)", // Dark gray background with 80% opacity
          }}
        />
        <CardContent>
          <QuestionnaireGroup questions={barriersNotApply} />
        </CardContent>
      </Card>
      <Grid container spacing={1} justifyContent="flex-end">
        <Grid item>
          <Button variant="contained" color="primary" onClick={handleBackClick}>
            {t("button_back")}
          </Button>
        </Grid>
        <Grid item>
          <Button variant="contained" color="primary" onClick={handleNextClick} disabled={!isValid}>
            {t("button_next")}
          </Button>
        </Grid>
      </Grid>
    </PageContent>
  );
};

export default ScreeningResults;
